/**
 * Bancos (Enable Banking): conectar cada cuenta con su banco (la autorización se hace siempre en la web del banco),
 * sincronizar (saldo y movimientos, hasta 4 veces al día) y revisar lo nuevo como un extracto más.
 */
import { BANKS, mapBankTransactions, pickBankBalance } from '@pf/domain';
import { isAdmin } from './adapters/session.js';
import { api, type AspspInfo, type BankConnectionView, type BankStatus } from './adapters/api-client.js';
import { adapterFor, minDateFor } from './app/import-model.js';
import { startReview } from './import-views.js';
import { $, esc, dayLabel, todayIso, money } from './ui/format.js';
import { toast, stickyBar } from './ui/feedback.js';
import { frame, setView } from './views.js';
import { pendingBanner, type EditState, type Editor } from './edit-views.js';

/** Mensajes que deja la vuelta del banco en la URL (?bank_ok / ?bank_error): se muestran una vez y se limpian. */
function returnMessage(): { ok: string; error: string; select: boolean } {
  const q = new URLSearchParams(location.search);
  const r = { ok: q.get('bank_ok') ?? '', error: q.get('bank_error') ?? '', select: q.has('bank_select') };
  if (r.ok || r.error) history.replaceState(null, '', `/${location.hash}`);
  return r;
}

const when = (iso: string) => (iso ? `${dayLabel(iso.slice(0, 10))} ${iso.slice(11, 16)}` : '');

function card(name: string, c: BankConnectionView | undefined, bankId: string): string {
  const head = `<h2>${esc(name)}${c?.accountLabel ? ` <span class="sub">${esc(c.accountLabel)}</span>` : ''}</h2>`;
  if (!c) return `<section class="card">${head}<div class="sub">Sin conectar. Conéctala para traer los movimientos sin descargar ficheros.</div>
    <div class="actions"><button class="btn small" data-connect="${esc(name)}" data-bank="${bankId}">Conectar con ${esc(BANKS[bankId]?.label ?? bankId)}</button></div></section>`;
  if (c.choices.length) return `<section class="card">${head}<div class="sub">El banco ha dado acceso a varias cuentas. ¿Cuál es «${esc(name)}»?</div>
    <div class="list">${c.choices.map(x => `<button class="row" data-select="${esc(name)}" data-uid="${esc(x.uid)}"><div class="main"><div class="title">${esc(x.label)}</div></div></button>`).join('')}</div></section>`;
  if (c.expired || !c.connected) return `<section class="card">${head}<div class="recon diff">La autorización del banco ha caducado. Vuelve a conectar (se hace en la web del banco).</div>
    <div class="actions"><button class="btn small" data-connect="${esc(name)}" data-bank="${c.bank}">Reconectar</button> <button class="btn small secondary" data-disconnect="${esc(name)}">Desconectar</button></div></section>`;
  const left = c.maxPerDay - c.syncsToday;
  return `<section class="card">${head}
    <div class="sub">✓ Conectada · autorización hasta el ${esc(dayLabel(c.validUntil))}${c.daysLeft !== null && c.daysLeft <= 14 ? ` <span class="chip warn">quedan ${c.daysLeft} días</span>` : ''}</div>
    <div class="sub">${c.lastSyncAt ? `Última sincronización: ${esc(when(c.lastSyncAt))}` : 'Aún no se ha sincronizado'} · ${left > 0 ? `te quedan ${left} de ${c.maxPerDay} hoy` : 'hoy ya no quedan (límite PSD2)'}</div>
    <div class="actions"><button class="btn small" data-sync="${esc(name)}" data-bank="${c.bank}"${left > 0 ? '' : ' disabled'}>Sincronizar</button>
      ${c.daysLeft !== null && c.daysLeft <= 14 ? `<button class="btn small secondary" data-connect="${esc(name)}" data-bank="${c.bank}">Renovar autorización</button>` : ''}
      <button class="btn small secondary" data-disconnect="${esc(name)}">Desconectar</button></div>
  </section>`;
}

export function viewBanks(s: EditState, _ed: Editor) {
  frame(s, { title: 'Bancos', tab: 'mas', back: true });
  stickyBar('');
  const back = returnMessage();
  if (back.ok) toast(back.select ? '✓ Conectado: elige cuál de las cuentas es' : `✓ ${back.ok} conectada con el banco`);
  if (back.error) toast(back.error, 'error');
  if (s.demo) return setView('<section class="card"><h2>Bancos</h2><div class="sub">En la demostración no se conecta con ningún banco.</div></section>');
  // La conexión con bancos (Enable Banking, modo restringido) solo está dada de alta para el administrador; los demás importan ficheros
  if (!isAdmin()) return setView('<section class="card"><h2>Bancos</h2><div class="sub">La conexión directa con el banco no está disponible para tu cuenta. Descarga el extracto en la web de tu banco e impórtalo desde <a href="#/importar">Importar extracto</a>.</div></section>');
  setView(`${pendingBanner(s)}<div class="loading">Consultando las conexiones…</div>`);
  const accounts = s.c.wb.accounts.filter(a => adapterFor(a.name, a.bank));
  const draw = (st: BankStatus) => {
    if (!st.configured) return setView(`<section class="card"><h2>Bancos</h2><div class="sub">La conexión con el banco aún no está configurada en el servidor (falta la clave de Enable Banking).</div></section>`);
    setView(`${pendingBanner(s)}
      <section class="card"><div class="sub">La app solo puede <b>leer</b> saldo y movimientos, nunca mover dinero. Tus claves del banco se escriben siempre en la web del banco, nunca aquí. Por norma (PSD2) cada cuenta se puede consultar hasta ${st.connections[0]?.maxPerDay ?? 4} veces al día y la autorización dura unos 6 meses.</div></section>
      ${accounts.map(a => card(a.name, st.connections.find(c => c.account === a.name), adapterFor(a.name, a.bank))).join('')}
      ${accounts.length ? '' : '<div class="empty">No hay cuentas de BBVA, ING, Laboral Kutxa o MyInvestor en tu libro</div>'}
      <section class="card"><details><summary class="sub">Diagnóstico: ficha del banco en Enable Banking</summary>
        <div class="sub hint" style="margin-top:8px">Solo consulta el catálogo de Enable Banking: no entra en el banco ni pide claves.</div>
        <div class="row" style="gap:8px;align-items:center;margin-top:8px"><select id="aspspBank">${st.banks.map(b => `<option value="${esc(b.id)}"${b.id === 'MYINVESTOR' ? ' selected' : ''}>${esc(b.label)}</option>`).join('')}</select>
          <button class="btn small secondary" data-aspsp="1">Ver ficha</button></div>
        <div id="aspspOut"></div></details></section>`);
    $('#view').onclick = onClick;   // setView quita los manejadores: se ponen después de cada dibujo
  };
  const load = async () => { try { draw(await api.bank.status()); } catch (err) { setView(`<div class="error">${esc((err as Error).message)}</div>`); } };
  const onClick = async (e: MouseEvent) => {
    const b = (e.target as HTMLElement).closest<HTMLButtonElement>('button');
    if (!b) return;
    const busy = (on: boolean) => { b.disabled = on; };
    try {
      if (b.dataset.connect) {
        busy(true);
        const { url } = await api.bank.connect(b.dataset.connect, b.dataset.bank!);
        location.href = url;   // a la web del banco; vuelve a /api/bank/callback
      } else if (b.dataset.select) {
        busy(true);
        await api.bank.select(b.dataset.select, b.dataset.uid!);
        toast('✓ Cuenta elegida');
        await load();
      } else if (b.dataset.disconnect) {
        if (!confirm(`¿Desconectar ${b.dataset.disconnect} del banco? Podrás volver a conectarla cuando quieras.`)) return;
        busy(true);
        await api.bank.disconnect(b.dataset.disconnect);
        toast('Desconectada');
        await load();
      } else if (b.dataset.aspsp) {
        busy(true);
        $('#aspspOut').innerHTML = '<div class="sub">Consultando el catálogo…</div>';
        $('#aspspOut').innerHTML = aspspHtml(await api.bank.aspsp(($('#aspspBank') as HTMLSelectElement).value));
        busy(false);
      } else if (b.dataset.sync) {
        busy(true);
        b.textContent = 'Consultando el banco…';
        const account = b.dataset.sync, bankId = b.dataset.bank!;
        const r = await api.bank.sync(account, minDateFor(s.c));
        const mapped = mapBankTransactions(r.transactions);
        const bal = pickBankBalance(r.balances);
        const bankBalance = bal ? { amount: bal.amount, date: bal.date || todayIso() } : null;
        if (!mapped.rows.length) {
          toast(`Sin movimientos nuevos desde el ${dayLabel(r.from)}${bankBalance ? ` · saldo ${money(bankBalance.amount)}` : ''}`);
          if (bankBalance) location.hash = `#/cuenta/${encodeURIComponent(account)}`;
          await load();
          return;
        }
        const warnings = [
          ...(mapped.pending ? [`${mapped.pending} movimiento(s) aún pendientes en el banco: llegarán en la próxima sincronización`] : []),
          ...(mapped.invalid ? [`${mapped.invalid} movimiento(s) del banco sin fecha, importe o concepto`] : []),
        ];
        startReview(s, { account, adapterId: bankId, fileName: `Sincronización ${todayIso()}`, rows: mapped.rows, warnings, source: 'sync:enablebanking', bankBalance });
      }
    } catch (err) {
      toast((err as Error).message, 'error');
      busy(false);
      void load();
    }
  };
  void load();
}

/** Cómo te pide el banco que te identifiques, en palabras. */
const APPROACH: Record<string, string> = {
  REDIRECT: 'te lleva a la web del banco para entrar',
  DECOUPLED: 'lo apruebas en la app del banco (sin escribir la clave en ninguna web)',
  EMBEDDED: 'las claves se piden dentro del agregador',
};

/** La ficha del banco: con qué nombre lo usa la app, sus métodos de acceso y lo que pide cada uno (y los datos técnicos, para soporte). */
function aspspHtml(r: AspspInfo): string {
  if (!r.matches.length) return `<div class="sub" style="margin-top:8px">Enable Banking no tiene ningún banco parecido a ${esc(r.label)} (${r.total} bancos en el catálogo).</div>`;
  return `<div style="margin-top:8px"><div class="sub">La app usa: <b>${esc(r.chosen ?? 'ninguno (no lo encuentra)')}</b> · ${r.total} bancos en el catálogo</div>
    ${r.matches.map(a => `<div class="st-g" style="margin-top:8px"><b>${esc(a.name)}</b>${a.beta ? ' <span class="sub">· en pruebas (beta)</span>' : ''}
      <div class="sub">${[a.bic && `BIC ${esc(a.bic)}`, a.psuTypes.length && `para ${esc(a.psuTypes.join(', '))}`, a.maxConsentDays && `permiso hasta ${a.maxConsentDays} días`,
        a.requiredPsuHeaders.length && `pide datos del navegador: ${esc(a.requiredPsuHeaders.join(', '))}`].filter(Boolean).join(' · ')}</div>
      ${a.authMethods.length ? a.authMethods.map(m => `<div class="st-it"><div>${esc(m.title || m.name)}${m.hidden ? ' <span class="sub">(oculto)</span>' : ''} <span class="sub">· ${esc(m.psuType || 'cualquiera')}</span></div>
        <div class="sub">${esc(APPROACH[m.approach] ?? m.approach)}${m.credentials.length ? ` · pide: ${m.credentials.map(c => `${esc(c.title || c.name)}${c.required ? '' : ' (opcional)'}`).join(', ')}` : ''}</div></div>`).join('')
        : '<div class="sub">Sin métodos de acceso publicados (usa el de por defecto).</div>'}</div>`).join('')}
    <details style="margin-top:8px"><summary class="sub">Datos técnicos (para soporte)</summary><pre class="aspsp-raw">${esc(JSON.stringify(r.matches, null, 2))}</pre></details></div>`;
}
